// `search-form/page/search.page.tpl.html` — the alert criteria builder's OUTER SHELL: the
// title bar, the nine collapsible sections and the footer CTA (plan §7 phase 3, slice 2).
//
// ⚠️ THE SHELL ONLY. The template is 1104 lines over an 8.8k-line tree and the plan calls it
// "the single largest component"; each section is a form of its own — a repeating
// field/position/years/when grid, a skills matrix, a location picker with metro regions, an
// ATS-filter builder. They land one at a time, and this is the frame they land in.
//
// It is worth having before them because it is what makes the sections independently
// verifiable: the shell renders at the real route with the real section titles, so a section
// added next can be diffed against the reference for that screen rather than against nothing.
//
// Two upstream behaviours the frame itself carries:
//
//   - the title bar and the footer are BOTH behind `state !== SHORTLIST`. The builder renders
//     inside the shortlist frame as well as standalone, and there it has neither — so a shell
//     that always drew them would add a header and a button to the embedded form.
//   - the last two sections are conditional: the ATS-filters section is titled with the
//     tenant's ATS name, and the external-sources section only exists when the tenant has
//     sources configured.
import type { ReactElement, ReactNode } from 'react'
import { WooCollapsible } from '../common/WooCollapsible.tsx'
import { asset } from '../assetBase.ts'

/** `ExtensionFormState` — the builder renders differently inside the shortlist frame. */
export const CriteriaFormState = {
  Standalone: 'standalone',
  Shortlist: 'shortlist',
} as const
export type CriteriaFormState = (typeof CriteriaFormState)[keyof typeof CriteriaFormState]

/**
 * The sections, in template order.
 *
 * ⚠️ `id`, `className` AND `wrapClassName` ARE ALL LISTED, because none of them derives from
 * another. The ids are the scroll anchors `onExtensionCriteriaNavigationClicked` jumps to, and
 * two of them do not match their title — "Skill set" is `skills-criteria-id` and "Keyword
 * search" is `keywords-criteria-id`. The wrap class is not `${className}-wrap` either: the
 * keywords section's is `keyword-wrap criteria-table`, SINGULAR and carrying a second class,
 * while contact-details has no wrap element at all. Deriving any of the three produces markup
 * that looks right and misses a rule.
 */
export const CRITERIA_SECTIONS = [
  {
    id: 'field-of-expertise-criteria-id',
    className: 'field-of-expertise',
    wrapClassName: 'field-of-expertise-wrap',
    title: 'Field of expertise',
  },
  { id: 'skills-criteria-id', className: 'skills', wrapClassName: 'skills-wrap', title: 'Skill set' },
  {
    id: 'work-experience-criteria-id',
    className: 'work-experience',
    wrapClassName: 'work-experience-wrap',
    title: 'Work experience',
  },
  { id: 'education-criteria-id', className: 'education', wrapClassName: 'education-wrap', title: 'Education' },
  { id: 'location-criteria-id', className: 'location', wrapClassName: 'location-wrap', title: 'Location' },
  // No wrap element upstream — the checkboxes are direct children of the fold.
  {
    id: 'contact-details-criteria-id',
    className: 'contact-details',
    wrapClassName: undefined,
    title: 'Contact details',
  },
  {
    id: 'keywords-criteria-id',
    className: 'keywords',
    wrapClassName: 'keyword-wrap criteria-table',
    title: 'Keyword search',
    // ⚠️ THE ONLY SECTION WITH A CONDITION, AND IT IS THE OPPOSITE OF THE TITLE BAR'S. Keyword
    // search renders `ng-if="state == SHORTLIST"` — embedded ONLY — while the title bar and the
    // footer render `ng-if="state !== SHORTLIST"`. So the standalone builder has a header, a
    // Find Candidates button and no keyword search; the embedded one has keyword search and
    // neither of the others. Rendering it in both places, which is what the six unconditional
    // siblings invite, adds a whole section to the standalone screen.
    embeddedOnly: true,
  },
] as const

export type CriteriaSectionId = (typeof CRITERIA_SECTIONS)[number]['id']

export interface CriteriaBuilderShellProps {
  state?: CriteriaFormState
  /**
   * Names the ATS-filters section — `collapsible-title="{{::ctrl.atsLabel}} filters"`.
   *
   * ⚠️ IT IS THE TITLE, NOT THE CONDITION. See `hasAtsFilters`.
   */
  atsLabel?: string
  /**
   * `atsFiltersCriteriaEnabled && configuration.fields.length > 0` — what actually decides
   * whether the ATS-filters section exists.
   *
   * ⚠️ A TENANT CAN HAVE AN ATS AND NO FILTERABLE FIELDS. Gating on `atsLabel`, which is
   * what this shell used to do because the title reads from it, shows an empty section with a
   * Field select that offers nothing — and adds a nav entry counting toward "Filters used".
   */
  hasAtsFilters?: boolean
  /** The external-sources section exists only when the tenant has any. */
  hasExternalSources?: boolean
  /** `ctrl.saveOnly` — the search CTA is replaced by an inline "create alert" form. */
  saveOnly?: boolean
  /** `ctrl.isAlertsEnabled` — gates the "Saved searches" link in the title bar. */
  alertsEnabled?: boolean
  /** Section content, by id. A section with none renders its fold and an empty body. */
  sections?: Partial<Record<string, ReactNode>>
  onClear?: () => void
  onStartSearch?: () => void
}

export function CriteriaBuilderShell({
  state = CriteriaFormState.Standalone,
  atsLabel,
  hasAtsFilters = false,
  hasExternalSources = false,
  saveOnly = false,
  alertsEnabled = false,
  sections = {},
  onClear,
  onStartSearch,
}: CriteriaBuilderShellProps): ReactElement {
  const embedded = state === CriteriaFormState.Shortlist

  return (
    <div className="chrome-extension-search-page">
      {embedded ? null : (
        <div className="extension-title">
          <div className="extension-title-container">
            <div className="title">
              Smart search <span className="title-by">by</span>
              <div className="logo-wrap">
                <img className="logo" src={asset('/static/assets/img/tagging/logo-blueish.svg')} alt="" />
              </div>
            </div>
            <div className="actions">
              <div className="action-btn actions-clear">
                <a
                  href=""
                  onClick={(event) => {
                    event.preventDefault()
                    onClear?.()
                  }}
                >
                  <i className="fa fa-eraser" /> CLEAR
                </a>
              </div>
              {alertsEnabled && !saveOnly ? (
                <div className="action-btn actions-create-alert">
                  {/* `create-alert-tooltip` opens the saved-search panel; that panel is its own
                      component and lands with the alert-creation flow. */}
                  <a href="" onClick={(event) => event.preventDefault()}>
                    <i className="fas fa-bell" /> Saved searches
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <div className="criteria-container">
        {CRITERIA_SECTIONS.filter((section) => ('embeddedOnly' in section ? embedded : true)).map((section) => (
          <WooCollapsible
            key={section.id}
            id={section.id}
            title={section.title}
            className={section.className}
            startOpen
          >
            {section.wrapClassName === undefined ? (
              sections[section.id]
            ) : (
              <div className={section.wrapClassName}>{sections[section.id]}</div>
            )}
          </WooCollapsible>
        ))}

        {!hasAtsFilters || atsLabel === undefined || atsLabel === '' ? null : (
          <WooCollapsible id="ats-filters-criteria-id" title={`${atsLabel} filters`} className="ats-filters" startOpen>
            <div className="ats-filters-wrap">{sections['ats-filters-criteria-id']}</div>
          </WooCollapsible>
        )}

        {hasExternalSources ? (
          <WooCollapsible
            id="external-source-filters-criteria-id"
            title="External sources filters"
            className="external-source-filters"
            startOpen
          >
            <div className="external-source-filters-wrap">{sections['external-source-filters-criteria-id']}</div>
          </WooCollapsible>
        ) : null}
      </div>

      {embedded ? null : (
        <div className="footer">
          <div className="footer-container">
            {saveOnly ? null : (
              <div className="action-btn actions-search">
                <button
                  type="button"
                  className="btn btn-primary mL15"
                  name="find-candidates-bottom"
                  onClick={onStartSearch}
                >
                  Find Candidates
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
